import { useState, useEffect, useMemo, useCallback } from 'react'
import { useIntelData } from '../../contexts/IntelDataContext'
import { Spinner } from '../../components/Spinner'
import type { LegendPayload } from 'recharts'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

interface ChartRow {
  month: string
  influencer_id: number
  influencer_name: string
  count: number
}

interface ChartDataPoint {
  month: string
  [kol: string]: number | string
}

interface ChartCardProps {
  title: string
  rawData: ChartRow[]
  timeframe: '3M' | '6M' | '1Y' | '2Y' | 'All'
}

const KOL_COLORS: Record<string, string> = {
  'Peter Schiff': '#ef4444',
  'Chris Martenson': '#3b82f6',
  'Eric Townsend': '#10b981',
  'Patrick Bet-David': '#f59e0b',
  'Killa': '#8b5cf6',
  'Ben Felix': '#ec4899',
  'Darin Soat': '#06b6d4',
}

function getCutoffMonth(monthsBack: number): string {
  const now = new Date()
  now.setMonth(now.getMonth() - monthsBack)
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

function filterByTimeframe(rawData: ChartRow[], timeframe: '3M' | '6M' | '1Y' | '2Y' | 'All'): ChartRow[] {
  if (timeframe === 'All') return rawData
  const monthsBack =
    timeframe === '3M' ? 3 :
    timeframe === '6M' ? 6 :
    timeframe === '1Y' ? 12 :
    24
  const cutoff = getCutoffMonth(monthsBack)
  return rawData.filter((row) => row.month >= cutoff)
}

function transformData(rawData: ChartRow[]) {
  if (!rawData || rawData.length === 0) return { chartData: [] as ChartDataPoint[], kols: [] as string[] }

  const monthsSet = new Set<string>()
  const kolsSet = new Set<string>()
  const counts: Record<string, Record<string, number>> = {}

  for (const row of rawData) {
    monthsSet.add(row.month)
    kolsSet.add(row.influencer_name)
    if (!counts[row.month]) counts[row.month] = {}
    counts[row.month][row.influencer_name] = row.count
  }

  const months = Array.from(monthsSet).sort()
  const kols = Array.from(kolsSet).sort()

  const chartData: ChartDataPoint[] = months.map((month) => {
    const point: ChartDataPoint = { month }
    for (const kol of kols) {
      point[kol] = counts[month]?.[kol] ?? 0
    }
    return point
  })

  return { chartData, kols }
}

function ChartCard({ title, rawData, timeframe }: ChartCardProps) {
  const filteredRawData = useMemo(() => filterByTimeframe(rawData, timeframe), [rawData, timeframe])
  const { chartData, kols } = useMemo(() => transformData(filteredRawData), [filteredRawData])
  const [hiddenKols, setHiddenKols] = useState<Set<string>>(new Set())

  const toggleKol = useCallback((kol: string) => {
    setHiddenKols((prev) => {
      const next = new Set(prev)
      if (next.has(kol)) {
        next.delete(kol)
      } else {
        next.add(kol)
      }
      return next
    })
  }, [])

  const handleLegendClick = useCallback((data: LegendPayload) => {
    if (data.value) {
      toggleKol(data.value)
    }
  }, [toggleKol])

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {chartData.length === 0 ? (
        <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">
          No data available
        </div>
      ) : (
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '0.875rem', cursor: 'pointer' }}
                onClick={handleLegendClick}
              />
              {kols.map((kol) => (
                <Line
                  key={kol}
                  type="monotone"
                  dataKey={kol}
                  stroke={KOL_COLORS[kol] || '#6b7280'}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  hide={hiddenKols.has(kol)}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

interface StatsResponse {
  episodes: ChartRow[]
  claims: ChartRow[]
  predictions: ChartRow[]
}

type Timeframe = '3M' | '6M' | '1Y' | '2Y' | 'All'

const TIMEFRAMES: Timeframe[] = ['3M', '6M', '1Y', '2Y', 'All']

export default function Stats() {
  const intel = useIntelData()
  const [data, setData] = useState<StatsResponse | null>((intel.statsCache?.data as StatsResponse) || null)
  const [error, setError] = useState<string | null>(null)
  const [timeframe, setTimeframe] = useState<Timeframe>('All')

  useEffect(() => {
    if (!intel.isStale(intel.statsCache?.timestamp)) return
    fetch('/api/intel/stats/charts')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((json: StatsResponse) => {
        setData(json)
        intel.cacheStats(json)
      })
      .catch((e: Error) => setError(e.message))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-xl sm:text-2xl font-bold mb-6 text-gray-900">Stats</h2>

      {error && (
        <div className="bg-red-50 text-red-700 rounded-lg p-4 mb-6 text-sm">
          Failed to load stats: {error}
        </div>
      )}

      {!data && !error && (
        <div className="bg-white border border-gray-200 rounded-lg h-[400px] flex flex-col items-center justify-center gap-3">
          <Spinner size={32} />
          <span className="text-sm text-gray-500">Loading charts...</span>
        </div>
      )}

      {data && (
        <div className="space-y-8">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 mr-1">Timeframe:</span>
            {TIMEFRAMES.map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={[
                  'px-3 py-1.5 rounded-md text-sm font-medium border transition-colors',
                  timeframe === tf
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
                ].join(' ')}
              >
                {tf}
              </button>
            ))}
          </div>
          <ChartCard title="Episodes Published per KOL" rawData={data.episodes} timeframe={timeframe} />
          <ChartCard title="Claims Extracted per KOL" rawData={data.claims} timeframe={timeframe} />
          <ChartCard title="Predictions Extracted per KOL" rawData={data.predictions} timeframe={timeframe} />
        </div>
      )}
    </div>
  )
}
